import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | error
  const [data, setData] = useState(null);
  const attempts = useRef(0);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!sessionId) {
      setState("error");
      return;
    }

    const poll = async () => {
      if (cancelled.current) return;
      attempts.current += 1;
      try {
        const res = await getPaymentStatus(sessionId);
        setData(res);
        if (res.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("expired");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("error");
        } else {
          setTimeout(poll, POLL_INTERVAL);
        }
      }
    };

    poll();
    return () => {
      cancelled.current = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white text-black font-body flex items-center justify-center px-6" data-testid="payment-success-root">
      <div className="max-w-xl w-full border-2 border-black p-8 lg:p-12 shadow-brutal">
        {state === "checking" && (
          <div className="text-center" data-testid="payment-checking">
            <Loader2 className="w-14 h-14 animate-spin mx-auto text-[#E60000]" />
            <h1 className="font-display text-4xl uppercase mt-6">Confirming payment…</h1>
            <p className="mt-3 text-neutral-700 font-medium">
              Hang tight — we're checking with Stripe. This usually takes a few seconds.
            </p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-paid">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#E60000]" strokeWidth={2.5} />
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E60000]">
                Payment confirmed
              </div>
            </div>
            <h1 className="font-display text-5xl lg:text-6xl uppercase leading-[0.9]">
              You took the<br />first step.
            </h1>
            <p className="mt-6 text-lg font-medium text-neutral-800">
              Thank you — your contribution keeps this project alive. If this was the ebook,
              a delivery email with your download will arrive within a few minutes.
              Check spam if you don't see it.
            </p>
            <div className="mt-8 border-2 border-black p-4 bg-[#F5F2EC] text-sm space-y-1">
              <div className="flex justify-between">
                <span className="uppercase tracking-widest font-bold text-xs">Session</span>
                <span className="font-mono text-xs truncate ml-4">{sessionId?.slice(0, 20)}…</span>
              </div>
              {data?.payment_status && (
                <div className="flex justify-between">
                  <span className="uppercase tracking-widest font-bold text-xs">Status</span>
                  <span className="text-xs font-bold text-[#E60000] uppercase">{data.payment_status}</span>
                </div>
              )}
            </div>
            <Link
              to="/"
              data-testid="payment-success-home"
              className="mt-8 inline-flex items-center justify-center w-full h-12 bg-black text-white border-2 border-black font-bold uppercase tracking-widest text-sm hover:bg-[#E60000] transition-colors"
            >
              Back to The Last Bet Project
            </Link>
          </div>
        )}
        {state === "expired" && (
          <div className="text-center" data-testid="payment-expired">
            <h1 className="font-display text-3xl uppercase">Still processing…</h1>
            <p className="mt-3 text-neutral-700 font-medium">
              Your payment is still being confirmed. Refresh in a minute or check your email.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center justify-center w-full h-12 bg-black text-white border-2 border-black font-bold uppercase tracking-widest text-sm hover:bg-[#E60000] transition-colors"
            >
              Back home
            </Link>
          </div>
        )}
        {state === "error" && (
          <div className="text-center" data-testid="payment-error">
            <XCircle className="w-14 h-14 mx-auto text-[#E60000]" />
            <h1 className="font-display text-3xl uppercase mt-6">Something went wrong</h1>
            <p className="mt-3 text-neutral-700 font-medium">
              We couldn't confirm your payment status. If you were charged, email us and we'll fix it.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center justify-center w-full h-12 bg-black text-white border-2 border-black font-bold uppercase tracking-widest text-sm hover:bg-[#E60000] transition-colors"
            >
              Back home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
